import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Lightning, Flame, Plus, Trash, Pencil, Copy, FilePdf, Lightning as Bolt,
  CheckCircle, MagnifyingGlass, ListChecks, FileXls, ToggleLeft, ToggleRight,
  DownloadSimple,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const TIPOLOGIA_LABELS = { luce: 'Luce', gas: 'Gas', dual: 'Dual' };
const TIPOLOGIA_ICONS = { luce: Lightning, gas: Flame, dual: Bolt };
const PREZZO_LABELS = { fisso: 'Fisso', indicizzato: 'Indicizzato' };
const CATEGORIA_LABELS = { domestico: 'Domestico', altri_usi: 'Altri Usi' };

export default function OffersPage() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTip, setFilterTip] = useState('all');
  const [filterStato, setFilterStato] = useState('all');
  const [filterVend, setFilterVend] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterVend) params.set('vendibile_oggi', 'true');
      const res = await fetch(`${API}/api/offers?${params}`, { credentials: 'include' });
      if (res.ok) setOffers(await res.json());
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, [filterVend]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = (filteredIds) => {
    if (filteredIds.every(id => selected.has(id))) {
      const next = new Set(selected);
      filteredIds.forEach(id => next.delete(id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      filteredIds.forEach(id => next.add(id));
      setSelected(next);
    }
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) return toast.error('Seleziona almeno una CTE');
    const labels = { activate: 'Attivare', deactivate: 'Disattivare', delete: 'ELIMINARE' };
    if (!window.confirm(`${labels[action]} ${selected.size} offerte?`)) return;
    try {
      const res = await fetch(`${API}/api/offers/bulk`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selected), action }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${data.affected} offerte ${action === 'delete' ? 'eliminate' : action === 'activate' ? 'attivate' : 'disattivate'}`);
        setSelected(new Set());
        fetchData();
      } else toast.error(data.detail || 'Errore');
    } catch (e) { toast.error('Errore bulk'); }
  };

  const exportCatalog = async () => {
    try {
      const res = await fetch(`${API}/api/offers/export/catalog`, { credentials: 'include' });
      if (!res.ok) { const d = await res.json(); return toast.error(d.detail); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `Catalogo_offerte_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      toast.success('Catalogo esportato');
    } catch (e) { toast.error('Errore'); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Eliminare CTE "${name}"?`)) return;
    try {
      const res = await fetch(`${API}/api/offers/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Eliminata'); fetchData(); }
    } catch (e) { toast.error('Errore'); }
  };

  const duplicate = async (id) => {
    try {
      const res = await fetch(`${API}/api/offers/${id}/duplicate`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (res.ok && data.id) {
        toast.success('Duplicata');
        navigate(`/offers/${data.id}/edit`);
      }
    } catch (e) { toast.error('Errore'); }
  };

  const generateCte = async (id) => {
    try {
      const res = await fetch(`${API}/api/offers/${id}/generate-cte`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) { const d = await res.json(); return toast.error(d.detail || 'Errore'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('CTE generata');
    } catch (e) { toast.error('Errore'); }
  };

  const generateScheda = async (id) => {
    try {
      const res = await fetch(`${API}/api/offers/${id}/scheda-sintetica`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) { const d = await res.json(); return toast.error(d.detail || 'Errore'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('Scheda sintetica generata');
    } catch (e) { toast.error('Errore'); }
  };

  const filtered = offers.filter(o =>
    (filterTip === 'all' || o.tipologia === filterTip) &&
    (filterStato === 'all' || (filterStato === 'attiva' ? o.attiva : !o.attiva)) &&
    (!search ||
      o.nome_offerta?.toLowerCase().includes(search.toLowerCase()) ||
      o.codice_sii?.toLowerCase().includes(search.toLowerCase()) ||
      o.codice_interno?.toLowerCase().includes(search.toLowerCase()))
  );

  const stats = {
    totale: offers.length,
    attive: offers.filter(o => o.attiva).length,
    luce: offers.filter(o => o.tipologia === 'luce').length,
    gas: offers.filter(o => o.tipologia === 'gas').length,
    dual: offers.filter(o => o.tipologia === 'dual').length,
  };

  if (loading) return <div className="p-8 text-slate-500">Caricamento...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="offers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Lightning size={32} weight="duotone" className="text-orange-500" />
            CTE Commerciali
          </h1>
          <p className="text-slate-500 mt-1">Crea e gestisci le CTE (Condizioni Tecnico-Economiche) luce/gas. Generazione automatica documento + Scheda Sintetica.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportCatalog} variant="outline" className="border-yellow-300 text-yellow-700"
                  data-testid="export-catalog-btn">
            <FileXls size={16} className="mr-1" /> Export Excel
          </Button>
          <Button onClick={() => navigate('/offers/new/edit')} className="bg-orange-500 hover:bg-orange-600" data-testid="create-offer-btn">
            <Plus size={18} className="mr-2" /> Nuova CTE
          </Button>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-5 gap-2 mb-4">
        <StatCard label="Totale" value={stats.totale} active={filterTip === 'all' && filterStato === 'all'}
                  onClick={() => { setFilterTip('all'); setFilterStato('all'); }} />
        <StatCard label="Attive" value={stats.attive} color="bg-green-50 text-green-700"
                  active={filterStato === 'attiva'} onClick={() => setFilterStato(filterStato === 'attiva' ? 'all' : 'attiva')} />
        <StatCard label="Luce" value={stats.luce} color="bg-yellow-50 text-yellow-700"
                  active={filterTip === 'luce'} onClick={() => setFilterTip(filterTip === 'luce' ? 'all' : 'luce')} />
        <StatCard label="Gas" value={stats.gas} color="bg-blue-50 text-blue-700"
                  active={filterTip === 'gas'} onClick={() => setFilterTip(filterTip === 'gas' ? 'all' : 'gas')} />
        <StatCard label="Dual" value={stats.dual} color="bg-purple-50 text-purple-700"
                  active={filterTip === 'dual'} onClick={() => setFilterTip(filterTip === 'dual' ? 'all' : 'dual')} />
      </div>

      {/* SEARCH BAR + Vendibili oggi filter */}
      <div className="bg-white border rounded-lg p-3 mb-4 flex items-center gap-2">
        <MagnifyingGlass size={16} className="text-slate-400 ml-2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Cerca per nome, codice SII o codice interno..."
               className="border-0 focus-visible:ring-0" data-testid="offers-search" />
        <Button size="sm" variant={filterVend ? 'default' : 'outline'}
                onClick={() => setFilterVend(!filterVend)}
                className={filterVend ? 'bg-blue-600 hover:bg-blue-700' : ''}
                data-testid="filter-vendibili-oggi">
          <CheckCircle size={14} className="mr-1" /> Vendibili oggi
        </Button>
      </div>

      {/* BULK TOOLBAR */}
      {selected.size > 0 && (
        <div className="bg-orange-50 border-orange-200 border rounded-lg p-3 mb-4 flex items-center justify-between"
             data-testid="bulk-toolbar">
          <div className="text-sm text-orange-800 font-medium">
            {selected.size} CTE selezionate
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => bulkAction('activate')}
                    className="border-green-300 text-green-700" data-testid="bulk-activate">
              <ToggleRight size={14} className="mr-1" /> Attiva
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulkAction('deactivate')}
                    className="border-slate-300 text-slate-700" data-testid="bulk-deactivate">
              <ToggleLeft size={14} className="mr-1" /> Disattiva
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulkAction('delete')}
                    className="border-red-300 text-red-700" data-testid="bulk-delete">
              <Trash size={14} className="mr-1" /> Elimina
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Annulla
            </Button>
          </div>
        </div>
      )}

      {/* LIST */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <Lightning size={64} className="mx-auto text-slate-300 mb-4" weight="duotone" />
          <p className="text-slate-500">
            {offers.length === 0 ? 'Nessuna CTE. Crea la prima per iniziare.' : 'Nessun risultato per i filtri selezionati'}
          </p>
        </div>
      ) : (
        <>
          {/* Select all */}
          <div className="flex items-center gap-2 mb-2 px-2">
            <input type="checkbox"
                   checked={filtered.length > 0 && filtered.every(o => selected.has(o.id))}
                   onChange={() => toggleSelectAll(filtered.map(o => o.id))}
                   className="cursor-pointer" data-testid="select-all" />
            <span className="text-xs text-slate-500">Seleziona tutte ({filtered.length})</span>
          </div>
          <div className="grid gap-3">
          {filtered.map(o => {
            const Icon = TIPOLOGIA_ICONS[o.tipologia] || Lightning;
            const isSel = selected.has(o.id);
            return (
              <div key={o.id} className={`bg-white rounded-lg border p-4 hover:shadow-md transition cursor-pointer ${
                                          isSel ? 'border-orange-400 ring-1 ring-orange-200' : 'border-slate-200 hover:border-orange-200'
                                        }`}
                   data-testid={`offer-card-${o.id}`}
                   onClick={(e) => {
                     if (e.target.tagName === 'INPUT') return;
                     navigate(`/offers/${o.id}/edit`);
                   }}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <input type="checkbox" checked={isSel}
                           onChange={(e) => { e.stopPropagation(); toggleSelect(o.id); }}
                           onClick={(e) => e.stopPropagation()}
                           className="mt-2 cursor-pointer" data-testid={`select-${o.id}`} />
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center
                      ${o.tipologia === 'luce' ? 'bg-yellow-100 text-yellow-600' :
                        o.tipologia === 'gas' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                      <Icon size={24} weight="duotone" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-slate-900">{o.nome_offerta}</h3>
                        <Badge variant="outline">{TIPOLOGIA_LABELS[o.tipologia]}</Badge>
                        <Badge variant="outline">{PREZZO_LABELS[o.tipo_prezzo]}</Badge>
                        <Badge variant="outline">{CATEGORIA_LABELS[o.categoria]}</Badge>
                        {o.attiva ? (
                          <Badge className="bg-green-100 text-green-700"><CheckCircle size={12} className="inline mr-1" />Attiva</Badge>
                        ) : <Badge variant="secondary">Inattiva</Badge>}
                        {o.vendibile_oggi && (
                          <Badge className="bg-blue-100 text-blue-700">Vendibile oggi</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 font-mono">
                        SII: {o.codice_sii}{o.codice_interno && ` · ${o.codice_interno}`}
                      </div>
                      {o.tipo_prezzo === 'indicizzato' && (
                        <div className="text-sm text-slate-600 mt-1">
                          Indice: <strong>{o.indice}</strong>
                          {o.fasce?.[0]?.spread && ` · Spread medio: ${o.fasce[0].spread} €/kWh`}
                        </div>
                      )}
                      {o.tipo_prezzo === 'fisso' && o.fasce?.length > 0 && (
                        <div className="text-sm text-slate-600 mt-1">
                          {o.fasce.filter(f => f.prezzo_fisso).map(f => `${f.codice}: ${f.prezzo_fisso}`).join(' · ')}
                        </div>
                      )}
                      {(o.validita_dal || o.validita_al) && (
                        <div className="text-xs text-slate-400 mt-1">
                          Validità: {o.validita_dal || '—'} → {o.validita_al || '—'}
                          {o.durata_offerta_mesi && ` · ${o.durata_offerta_mesi} mesi`}
                          {o.history_count > 0 && ` · ${o.history_count} eventi storico`}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="outline" onClick={() => generateCte(o.id)}
                            title="Genera CTE PDF" data-testid={`generate-cte-${o.id}`}
                            className="text-red-600 border-red-200">
                      <FilePdf size={16} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => generateScheda(o.id)}
                            title="Scheda Sintetica" data-testid={`generate-scheda-${o.id}`}
                            className="text-blue-600 border-blue-200">
                      <ListChecks size={16} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => duplicate(o.id)} title="Duplica">
                      <Copy size={16} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/offers/${o.id}/edit`)} title="Modifica">
                      <Pencil size={16} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(o.id, o.nome_offerta)}
                            className="text-red-600" title="Elimina">
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, color = 'bg-slate-50 text-slate-700', active, onClick }) {
  return (
    <div onClick={onClick}
         className={`bg-white border rounded-lg p-3 cursor-pointer transition ${active ? 'ring-2 ring-orange-500' : 'hover:bg-slate-50'}`}
         data-testid={`stat-${label.toLowerCase()}`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-2xl font-bold inline-block px-2 rounded ${color}`}>{value}</div>
    </div>
  );
}
